import { createContext, useContext } from 'react'
import type { Language, AvatarGender, InterviewConfig } from '@/types/interview'
import type { useInterview } from '@/hooks/useInterview'

// Shape both hooks (useInterview, useJoinInterview) must satisfy so the
// polished screens render against either flow. `setup` is intentionally
// excluded — its signature differs between the two hooks (candidate takes
// an InterviewConfig, join takes optional override credentials) and only
// the owning layout calls it. Screens never see setup via context.
export type InterviewController = Omit<ReturnType<typeof useInterview>, 'setup'>

export interface InterviewSessionContextValue {
  language: Language
  interview: InterviewController
  loadedConfig?: Partial<InterviewConfig>            // candidate-flow only
  redirectUrl?: string | null                        // join-flow only: post-interview redirect from template
  handleStart: () => Promise<void>
  handleNext: () => void
  handleEnd: () => void
  handleContinue: () => void
  handleBackToHome: () => void
  // Candidate-only handlers. Screens that depend on them
  // (LanguageScreen, AvatarScreen) read them via guards.
  handleBackToLanguage?: () => void
  handleBackToAvatar?: () => void
  handleSelectLanguage?: (lang: Language) => void
  handleSelectAvatar?: (gender: AvatarGender) => void
}

export const InterviewSessionContext =
  createContext<InterviewSessionContextValue | null>(null)

export function useInterviewSessionContext(): InterviewSessionContextValue {
  const ctx = useContext(InterviewSessionContext)
  if (!ctx) {
    throw new Error(
      'useInterviewSessionContext must be used within an InterviewSessionContext.Provider'
    )
  }
  return ctx
}
